import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryLabel,
  VictoryLine,
} from 'victory';
import { RSIItem } from '../../../screens/ProfitCalculations/types';
import { useMemo } from 'react';
import { XAxisInfo } from '../../MultiChart';
import IndicatorLabel from '../IndicatorLabel';
import { colors } from '../../../colors';

interface Props {
  data: RSIItem[];
  isZoningBand: boolean;
  label: string;

  width?: number | undefined;
  height?: number | undefined;
  xAxisInfo?: XAxisInfo | undefined;
  showXAxis?: boolean;
}

const ChannelOverExtentionChart = (props: Props) => {
  const { data, isZoningBand, width, height, label, xAxisInfo, showXAxis } =
    props;

  const dataPoints = useMemo(() => {
    if (!data || data?.length < 2) {
      return [];
    }

    /** Because the candles start at the beginning of the candle
     * and the rsi's value is al the end of the candle
     * We have to change position a little
     */
    const range =
      data[data.length - 1].timestamp - data[data.length - 2].timestamp;

    return data.map((d) => ({ x: d.timestamp - range, y: d.value })) ?? [];
  }, [data]);

  const yAxisStyle = useMemo(
    () => ({
      axis: { stroke: 'rgba(64,64,64,0.7)' },
      grid: {
        stroke: 'rgba(64,64,64,0.7)',
      },
      ticks: { stroke: 'white', size: 2 },
      tickLabels: { fontSize: 14, padding: 5, fill: '#999' },
    }),
    [],
  );

  const yTickValues = useMemo(() => {
    if (isZoningBand) {
      return [-90, -60, -30, 0, 30, 60, 90];
    }

    return [-100, -70, -40, 0, 40, 70, 100];
  }, [isZoningBand]);

  const middleBandDataPoints = useMemo(() => {
    if (!xAxisInfo?.domain.x.length) {
      return [];
    }

    const middleBandThreshold = isZoningBand ? 60 : 70;

    return [
      {
        x: xAxisInfo?.domain.x[0],
        y: middleBandThreshold,
        y0: -middleBandThreshold,
      },
      {
        x: xAxisInfo?.domain.x[1],
        y: middleBandThreshold,
        y0: -middleBandThreshold,
      },
    ];
  }, [isZoningBand, xAxisInfo?.domain.x]);

  const sellBandDataPoints = useMemo(() => {
    if (!isZoningBand || !xAxisInfo?.domain.x.length) {
      return undefined;
    }

    return [
      {
        x: xAxisInfo?.domain.x[0],
        y: 90,
        y0: 60,
      },
      {
        x: xAxisInfo?.domain.x[1],
        y: 90,
        y0: 60,
      },
    ];
  }, [isZoningBand, xAxisInfo?.domain.x]);

  const buyBandDataPoints = useMemo(() => {
    if (!isZoningBand || !xAxisInfo?.domain.x.length) {
      return undefined;
    }

    return [
      {
        x: xAxisInfo?.domain.x[0],
        y: -60,
        y0: -90,
      },
      {
        x: xAxisInfo?.domain.x[1],
        y: -60,
        y0: -90,
      },
    ];
  }, [isZoningBand, xAxisInfo?.domain.x]);

  const gradientTopOffset = useMemo(() => {
    if (!dataPoints.length) {
      return 0;
    }

    const max = Math.max(...dataPoints.map((d) => d.y));
    const min = Math.min(...dataPoints.map((d) => d.y));
    const domainYRange = max - min;
    const topOffset = (max - 75) / domainYRange;

    return topOffset;
  }, [dataPoints]);

  const gradientBottomOffset = useMemo(() => {
    if (!dataPoints.length) {
      return 0;
    }

    const max = Math.max(...dataPoints.map((d) => d.y));
    const min = Math.min(...dataPoints.map((d) => d.y));
    const domainYRange = max - min;
    const topOffset = 1 - (0 - 75 - min) / domainYRange;

    return topOffset;
  }, [dataPoints]);

  const chartWidth = width;
  const chartHeight = height || 120;
  const gradientId = `Price_Over_Extension_gradient_${Math.random()}`;

  return (
    <div
      className="candlesticks-container"
      style={{
        width: chartWidth,
        height: chartHeight,
      }}
    >
      <IndicatorLabel label={label} />
      <svg style={{ position: 'absolute' }}>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0" stopColor="green" />
            <stop offset={gradientTopOffset} stopColor="gray" />
            <stop offset={gradientBottomOffset} stopColor="gray" />
            <stop offset="1" stopColor={colors.red} />
          </linearGradient>
        </defs>
      </svg>
      <VictoryChart
        width={chartWidth}
        domain={{ ...xAxisInfo?.domain, y: [-110, 110] }}
        domainPadding={xAxisInfo?.domainPadding}
        height={chartHeight}
        padding={{
          ...xAxisInfo?.chartPadding,
          top: 0,
          bottom: showXAxis ? 30 : 0,
        }}
      >
        <VictoryAxis
          tickFormat={showXAxis ? xAxisInfo?.tickFormat : () => ''}
          style={{ ...xAxisInfo?.axisStyle, ...(!showXAxis && { ticks: {} }) }}
          tickValues={xAxisInfo?.tickValues}
          tickLabelComponent={<VictoryLabel dy={chartHeight / 2.5} />}
        />
        <VictoryAxis
          dependentAxis
          style={yAxisStyle}
          tickFormat={(t) => `${t}%`}
          orientation={'right'}
          tickLabelComponent={<VictoryLabel dx={5} />}
          tickValues={yTickValues}
        />

        {sellBandDataPoints && (
          <VictoryArea
            data={sellBandDataPoints}
            style={{
              data: {
                fill: `${colors.red}25`,
              },
            }}
          />
        )}
        {middleBandDataPoints && (
          <VictoryArea
            data={middleBandDataPoints}
            style={{
              data: {
                fill: `${colors.ligthBlue}25`,
              },
            }}
          />
        )}
        {buyBandDataPoints && (
          <VictoryArea
            data={buyBandDataPoints}
            style={{
              data: {
                fill: `${colors.green}25`,
              },
            }}
          />
        )}

        <VictoryLine
          data={dataPoints}
          style={{
            data: { stroke: `url(#${gradientId})` },
          }}
        />
      </VictoryChart>
    </div>
  );
};

export default ChannelOverExtentionChart;
